import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiPhone, FiUser, FiUsers, FiAlertCircle, FiCheck } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Counseling() {
  useAuth();
  const [counsels, setCounsels] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCounsel, setEditingCounsel] = useState(null);
  const [filters, setFilters] = useState({
    studentId: '',
    type: '',
    startDate: '',
    endDate: ''
  });
  const [formData, setFormData] = useState({
    studentId: '',
    type: 'parent',
    date: format(new Date(), 'yyyy-MM-dd'),
    content: '',
    method: 'phone',
    followUpRequired: false,
    followUpDate: '',
    followUpNote: ''
  });

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filters.studentId) params.studentId = filters.studentId;
      if (filters.type) params.type = filters.type;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const [counselsRes, studentsRes] = await Promise.all([
        api.get('/api/counsel', { params }),
        api.get('/api/users', { params: { role: 'student' } })
      ]);
      setCounsels(counselsRes.data.counsels || []);
      setStudents(studentsRes.data.users || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.studentId, filters.type, filters.startDate, filters.endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = (studentId = '') => {
    setEditingCounsel(null);
    setFormData({
      studentId: studentId,
      type: 'parent',
      date: format(new Date(), 'yyyy-MM-dd'),
      content: '',
      method: 'phone',
      followUpRequired: false,
      followUpDate: '',
      followUpNote: ''
    });
    setShowModal(true);
  };

  const openEditModal = (counsel) => {
    setEditingCounsel(counsel);
    setFormData({
      studentId: counsel.studentId?._id || counsel.studentId,
      type: counsel.type,
      date: format(new Date(counsel.date), 'yyyy-MM-dd'),
      content: counsel.content,
      method: counsel.method,
      followUpRequired: counsel.followUpRequired || false,
      followUpDate: counsel.followUpDate ? format(new Date(counsel.followUpDate), 'yyyy-MM-dd') : '',
      followUpNote: counsel.followUpNote || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        followUpDate: formData.followUpRequired && formData.followUpDate ? formData.followUpDate : null,
        followUpNote: formData.followUpRequired ? formData.followUpNote : null
      };

      if (editingCounsel) {
        await api.put(`/api/counsel/${editingCounsel._id}`, payload);
        alert('수정되었습니다');
      } else {
        await api.post('/api/counsel', payload);
        alert('상담 기록이 등록되었습니다');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '처리에 실패했습니다');
    }
  };

  const handleDelete = async (counsel) => {
    if (!window.confirm('이 상담 기록을 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/api/counsel/${counsel._id}`);
      alert('삭제되었습니다');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '삭제에 실패했습니다');
    }
  };

  const handleCompleteFollowUp = async (counsel) => {
    if (!window.confirm('후속조치를 완료 처리하시겠습니까?')) return;

    try {
      await api.post(`/api/counsel/${counsel._id}/complete-follow-up`);
      alert('완료 처리되었습니다');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '처리에 실패했습니다');
    }
  };

  const getTypeLabel = (type) => {
    return type === 'parent' ? '학부모 상담' : '학생 상담';
  };

  const getMethodLabel = (method) => {
    const labels = {
      phone: '전화',
      visit: '방문',
      message: '문자/카톡',
      other: '기타'
    };
    return labels[method] || method;
  };

  // Stats
  const stats = {
    total: counsels.length,
    parent: counsels.filter(c => c.type === 'parent').length,
    student: counsels.filter(c => c.type === 'student').length,
    followUp: counsels.filter(c => c.followUpRequired && !c.followUpCompleted).length
  };

  return (
    <div className="counseling-page">
      <div className="page-header">
        <h2>상담 내역</h2>
        <button className="btn btn-primary" onClick={() => openCreateModal()}>
          <FiPlus /> 상담 기록
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">전체 상담</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.parent}</div>
          <div className="stat-label">학부모 상담</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.student}</div>
          <div className="stat-label">학생 상담</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.followUp}</div>
          <div className="stat-label">후속조치 필요</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>학생</label>
          <select
            value={filters.studentId}
            onChange={(e) => setFilters({ ...filters, studentId: e.target.value })}
          >
            <option value="">전체</option>
            {students.map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>유형</label>
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          >
            <option value="">전체</option>
            <option value="parent">학부모</option>
            <option value="student">학생</option>
          </select>
        </div>
        <div className="filter-group">
          <label>시작일</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>종료일</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
        {(filters.studentId || filters.type || filters.startDate || filters.endDate) && (
          <button
            className="btn btn-sm btn-outline"
            onClick={() => setFilters({ studentId: '', type: '', startDate: '', endDate: '' })}
          >
            <FiX /> 필터 초기화
          </button>
        )}
      </div>

      {/* 현재 필터 상태 표시 */}
      {(filters.startDate || filters.endDate) && (
        <div className="filter-summary">
          조회 기간: {filters.startDate || '처음'} ~ {filters.endDate || '현재'}
          {filters.studentId && ` | 학생: ${students.find(s => s._id === filters.studentId)?.name || '-'}`}
          {filters.type && ` | 유형: ${getTypeLabel(filters.type)}`}
        </div>
      )}

      {/* Counsels List */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>날짜</th>
              <th>학생</th>
              <th>유형</th>
              <th>방법</th>
              <th>내용</th>
              <th>작성자</th>
              <th>후속조치</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="loading">로딩 중...</td></tr>
            ) : counsels.length === 0 ? (
              <tr><td colSpan="8" className="empty">상담 기록이 없습니다</td></tr>
            ) : (
              counsels.map((counsel) => (
                <tr key={counsel._id} className={counsel.followUpRequired ? 'highlight' : ''}>
                  <td>{format(new Date(counsel.date), 'MM/dd')}</td>
                  <td>
                    <div className="student-name">
                      <FiUser className="icon" />
                      {counsel.studentId?.name || '-'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${counsel.type === 'parent' ? 'badge-primary' : 'badge-info'}`}>
                      {counsel.type === 'parent' ? <FiUsers /> : <FiUser />}
                      {getTypeLabel(counsel.type)}
                    </span>
                  </td>
                  <td>
                    <span className="badge badge-outline">
                      {counsel.method === 'phone' && <FiPhone />}
                      {getMethodLabel(counsel.method)}
                    </span>
                  </td>
                  <td className="content-cell">
                    <div className="truncate" title={counsel.content}>
                      {counsel.content}
                    </div>
                  </td>
                  <td>{counsel.createdBy?.name || '-'}</td>
                  <td>
                    {counsel.followUpRequired ? (
                      <div className="follow-up-info">
                        {counsel.followUpCompleted ? (
                          <span className="badge badge-success">
                            <FiCheck />
                            완료
                          </span>
                        ) : (
                          <>
                            <span className="badge badge-warning">
                              <FiAlertCircle />
                              {counsel.followUpDate ? format(new Date(counsel.followUpDate), 'MM/dd') : '필요'}
                            </span>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleCompleteFollowUp(counsel)}
                              title="완료 처리"
                              style={{ marginLeft: '4px', padding: '2px 6px', fontSize: '0.75rem' }}
                            >
                              <FiCheck /> 완료
                            </button>
                          </>
                        )}
                        {counsel.followUpNote && (
                          <div className="follow-up-note" title={counsel.followUpNote}>
                            {counsel.followUpNote}
                          </div>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-icon" onClick={() => openEditModal(counsel)} title="수정">
                        <FiEdit2 />
                      </button>
                      <button className="btn btn-icon btn-danger" onClick={() => handleDelete(counsel)} title="삭제">
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCounsel ? '상담 수정' : '상담 기록'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>학생 *</label>
                  <select
                    value={formData.studentId}
                    onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                    required
                  >
                    <option value="">선택하세요</option>
                    {students.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>상담 일자 *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>상담 유형 *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="parent">학부모 상담</option>
                    <option value="student">학생 상담</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>상담 방법 *</label>
                  <select
                    value={formData.method}
                    onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                  >
                    <option value="phone">전화</option>
                    <option value="visit">방문</option>
                    <option value="message">문자/카톡</option>
                    <option value="other">기타</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>상담 내용 *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="상담 내용을 입력하세요"
                  rows={5}
                  required
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.followUpRequired}
                    onChange={(e) => setFormData({ ...formData, followUpRequired: e.target.checked })}
                  />
                  후속 조치 필요
                </label>
              </div>

              {formData.followUpRequired && (
                <div className="form-row">
                  <div className="form-group">
                    <label>후속 조치 예정일</label>
                    <input
                      type="date"
                      value={formData.followUpDate}
                      onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>후속 조치 메모</label>
                    <input
                      type="text"
                      value={formData.followUpNote}
                      onChange={(e) => setFormData({ ...formData, followUpNote: e.target.value })}
                      placeholder="후속 조치 내용"
                    />
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCounsel ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
